/**
 * Input section for the Test Suite Configuration page.
 *
 * Provides text areas for business knowledge and root question samples,
 * plus a "Generate Config" button that triggers LLM pre-population.
 * Displays inline validation errors, loading state, and error alerts.
 *
 * @see Requirements 3.1–3.6, 4.1–4.2
 */

import React from 'react';
import {
  Container,
  Header,
  FormField,
  Textarea,
  Button,
  SpaceBetween,
  Alert,
  StatusIndicator,
  Box,
} from '@amzn/awsui-components-react';
import {
  ConfigGeneratorApiError,
  ConfigPageAction,
  ValidationErrors,
} from './types';

// ─── Props ─────────────────────────────────────────────────────────────

export interface InputSectionProps {
  businessKnowledge: string;
  rootQuestions: string;
  hasToolSelected: boolean;
  dispatch: React.Dispatch<ConfigPageAction>;
  onGenerateConfig: () => Promise<void>;
  isLoading: boolean;
  error: ConfigGeneratorApiError | null;
  validationErrors: ValidationErrors;
}

// ─── Constants ─────────────────────────────────────────────────────────

const BUSINESS_KNOWLEDGE_PLACEHOLDER =
  'Describe the business domain, terminology, product names, metric definitions...';
const ROOT_QUESTIONS_PLACEHOLDER =
  'Enter sample questions, one per line...';
const BUSINESS_KNOWLEDGE_ROWS = 6;
const ROOT_QUESTIONS_ROWS = 4;
const LLM_TIMEOUT_STATUS = 502;

// ─── Component ─────────────────────────────────────────────────────────

const InputSection: React.FC<InputSectionProps> = ({
  businessKnowledge,
  rootQuestions,
  hasToolSelected,
  dispatch,
  onGenerateConfig,
  isLoading,
  error,
  validationErrors,
}) => {
  const isGenerateEnabled =
    hasToolSelected &&
    businessKnowledge.trim().length > 0 &&
    rootQuestions.trim().length > 0 &&
    !isLoading;

  const handleBusinessKnowledgeChange = (value: string) => {
    dispatch({ type: 'SET_BUSINESS_KNOWLEDGE', value });
  };

  const handleRootQuestionsChange = (value: string) => {
    dispatch({ type: 'SET_ROOT_QUESTIONS', value });
  };

  const isLLMTimeout = error?.statusCode === LLM_TIMEOUT_STATUS;

  return (
    <Container header={<Header variant="h2">Business Context</Header>}>
      <SpaceBetween size="l">
        <FormField
          label="Business knowledge"
          description="Provide context about the business domain so the LLM can generate relevant configuration"
          errorText={validationErrors.businessKnowledge}
          constraintText="Include terminology, product names, metric definitions, and any domain-specific language"
        >
          <Textarea
            value={businessKnowledge}
            onChange={({ detail }) => handleBusinessKnowledgeChange(detail.value)}
            placeholder={BUSINESS_KNOWLEDGE_PLACEHOLDER}
            rows={BUSINESS_KNOWLEDGE_ROWS}
            disabled={isLoading}
            ariaLabel="Business knowledge"
          />
        </FormField>

        <FormField
          label="Root question samples"
          description="Provide example questions that represent the types of queries users will ask"
          errorText={validationErrors.rootQuestions}
          constraintText="Enter one question per line"
        >
          <Textarea
            value={rootQuestions}
            onChange={({ detail }) => handleRootQuestionsChange(detail.value)}
            placeholder={ROOT_QUESTIONS_PLACEHOLDER}
            rows={ROOT_QUESTIONS_ROWS}
            disabled={isLoading}
            ariaLabel="Root question samples"
          />
        </FormField>

        {error && (
          <Alert
            type="error"
            header={isLLMTimeout ? 'LLM service timeout' : 'Pre-population failed'}
            action={
              <Button onClick={onGenerateConfig} disabled={isLoading}>
                Retry
              </Button>
            }
          >
            {isLLMTimeout
              ? 'The LLM service timed out while generating the configuration. Please try again.'
              : error.detail || 'An unexpected error occurred during configuration generation.'}
          </Alert>
        )}

        <Box>
          <SpaceBetween size="s" direction="horizontal" alignItems="center">
            <Button
              variant="primary"
              onClick={onGenerateConfig}
              disabled={!isGenerateEnabled}
              loading={isLoading}
              ariaLabel="Generate configuration"
            >
              Generate Config
            </Button>
            {isLoading && (
              <StatusIndicator type="loading">
                Generating configuration...
              </StatusIndicator>
            )}
          </SpaceBetween>
        </Box>
      </SpaceBetween>
    </Container>
  );
};

export default InputSection;
